'use client';

import { Trophy, Users, Globe, MapPin, UserRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { LeaderboardTab } from '../types';

const TAB_DEFS: Record<LeaderboardTab, { labelKey: string; icon: typeof Trophy }> = {
  participants: { labelKey: 'tabParticipants', icon: Trophy },
  passengers: { labelKey: 'tabPassengers', icon: UserRound },
  teams: { labelKey: 'tabTeams', icon: Users },
  countries: { labelKey: 'tabCountries', icon: Globe },
  cities: { labelKey: 'tabCities', icon: MapPin },
};

export function LeaderboardTabs({
  tabs,
  labelKeys,
  activeTab,
  onTabChange,
}: {
  tabs: LeaderboardTab[];
  labelKeys?: Partial<Record<LeaderboardTab, string>>;
  activeTab: LeaderboardTab;
  onTabChange: (tab: LeaderboardTab) => void;
}) {
  const t = useTranslations('leaderboard');

  return (
    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
      {tabs.map((id) => {
        const def = TAB_DEFS[id];
        const Icon = def.icon;
        const active = activeTab === id;
        return (
          <button
            key={id}
            onClick={() => onTabChange(id)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium whitespace-nowrap rounded-lg transition-all touch-manipulation',
              active
                ? 'bg-primary text-white shadow-sm'
                : 'text-stone-700 hover:text-stone-800 hover:bg-stone-100',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className={cn('sm:inline', active ? 'inline' : 'hidden')}>
              {t(labelKeys?.[id] ?? def.labelKey)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
